import React, { useState, useRef, useEffect } from "react"
import styled from "@emotion/styled"

import { segmentEvent } from "@nutrafol/nutrafol-ui-kit/utils/segmentfunctions"
import theme from "@nutrafol/nutrafol-ui-kit/styles/theme"
import { queries } from "@nutrafol/nutrafol-ui-kit/styles/mediaqueries"

import Subtitle from "./subtitle"

import MuteIcon from "../../../../assets/icons/wild/mute-icon-white.svg"
import PlayIcon from "../../../../assets/icons/wild/video-play-icon.svg"
import PlayIconBlack from "../../../../assets/icons/wild/play-icon.svg"
import PauseIcon from "../../../../assets/icons/wild/video-pause-icon.svg"
import { pauseAllVideos } from "../../../../utils/general"

const VideoWrap = styled.div`
  position: relative;
  height: 100%;
  .pause-button {
    display: none;
  }
  .play-button {
    display: ${(props) => (props.showPlayButtonMobile ? "block" : "none")};
  }
  @media (min-width: 1024px) {
    .play-button {
      display: block;
    }
    &.active {
      &:hover {
        .pause-button {
          display: block;
        }
      }
    }
  }
  video {
    width: 100%;
    ${queries.medium`
      position: absolute;
      top: 50%;
      transform: translateY(-50%);
    `}
  }
`

const VideoControls = styled.div`
  cursor: pointer;
  position: absolute;
  width: 100%;
  height: 100%;
  z-index: 3;
`

const MuteWrap = styled.div`
  position: absolute;
  z-index: 4;
  top: 16px;
  left: 16px;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  background: ${theme.wild.color.master.secondary.secondary};
  svg {
    margin: 10px;
  }
  ${queries.large`
    width: 64px;
    height: 64px;
    svg {
      margin: 21px;
    }
  `}
`

const PlayPauseWrap = styled.div`
  position: absolute;
  height: 100%;
  width: 100%;
  svg {
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
  }

  .play-icon-container {
    position: absolute;
    top: 50%;
    left: 50%;
    height: 74px;
    width: 74px;
    transform: translate(-50%, -50%);
    background-color: rgba(255, 255, 255, 0.5);
    border-radius: 100%;
    box-shadow: 0px 22.298906326293945px 30.4075984954834px 0px #00000008;
    display: flex;
    justify-content: center;
    align-items: center;
    @media (min-width: ${theme.mediaqueries.small}) {
      height: 66px;
      width: 66px;
    }
    @media (min-width: ${theme.mediaqueries.medium}) {
      height: 108px;
      width: 108px;
    }

    svg {
      width: 22px;
      height: 29px;
      margin-left: 2px;
      @media (min-width: ${theme.mediaqueries.small}) {
        width: 19px;
        height: 26px;
      }
      @media (min-width: ${theme.mediaqueries.medium}) {
        width: 32px;
        height: 44px;
      }

      path {
        fill: ${theme.color.white};
      }
    }
  }
`
/*
 * 
 * const ProgresBarWrap = styled.div`
 *   position: absolute;
 *   height: 4px;
 *   background: #fff;
 *   width: calc(100% - 16px);
 *   left: 8px;
 *   bottom: 8px;
 * `
 * const Progress = styled.div`
 *   background: ${theme.wild.color.master.secondary.secondary};
 *   height: 4px;
 *   border-radius: 4px;
 * `
 */

const Video = ({
  video,
  poster,
  hasSound,
  subtitles,
  videoName,
  autoplay = true,
  showPlayButtonMobile = false,
  showTransparentButton = false,
  startMuted = true,
}) => {
  const [progressInSeconds, setProgressInSeconds] = useState(0)
  // const [progressPercentage, setProgressPercentage] = useState(0)
  const [currentSubtitle, setCurrentSubtitle] = useState()
  const [muted, setMuted] = useState(startMuted)
  const [playing, setPlaying] = useState(autoplay)
  const videoPlayerRef = useRef(null)
  const [observer, setObserver] = useState(null)

  useEffect(() => {
    const newObserver = new IntersectionObserver(([entry]) => {
      //console.log("entry.isIntersecting", entry.isIntersecting)
      if (entry.isIntersecting && autoplay) {
        playVideo()
      } else {
        pauseVideo()
      }
    })

    setObserver(newObserver)

    const handlePlay = () => {
      //console.log("Video event playing")
      setPlaying(true)
    }
    const handlePause = () => {
      //console.log("Video event paused")
      setPlaying(false)
    }

    videoPlayerRef?.current?.addEventListener("play", handlePlay)
    videoPlayerRef?.current?.addEventListener("pause", handlePause)

    return () => {
      try {
        newObserver.disconnect()
        videoPlayerRef?.current?.removeEventListener("play", handlePlay)
        videoPlayerRef?.current?.removeEventListener("pause", handlePause)
      } catch (error) {
        //
      }
    }
  }, [])

  useEffect(() => {
    if (observer && videoPlayerRef.current) {
      const timeoutId = setTimeout(() => {
        observer.observe(videoPlayerRef.current)
      }, 250)

      return () => {
        clearTimeout(timeoutId)
        observer.disconnect()
      }
    }
  }, [observer])

  const playVideo = () => {
    //console.log('playVideo')
    if (videoPlayerRef?.current) {
      segmentEvent("Video Played", {
        video_name: videoName,
        module_state: "Play",
      })
      pauseAllVideos()
      try {
        videoPlayerRef?.current?.play()
      } catch (error) {
        console.error("playVideo error", error)
      }
    }
  }

  const pauseVideo = () => {
    //console.log('pauseVideo')
    if (videoPlayerRef?.current) {
      segmentEvent("Video Played", {
        video_name: videoName,
        module_state: "Pause",
      })
      setTimeout(() => {
        try {
          videoPlayerRef?.current.pause()
        } catch (error) {
          console.error("pauseVideo error", error)
        }
      }, 50)
    }
  }

  const handleMuteIconClick = () => {
    setMuted(!muted)
  }

  const handlePlayPauseClick = () => {
    if (playing) {
      pauseVideo()
      try {
        observer.disconnect()
      } catch (error) {
        console.error("disconnect error", error)
      }
    } else {
      playVideo()
    }
  }

  const handleProgress = (e) => {
    if (e.target.duration > 0) {
      const seconds = parseInt(e.target.currentTime)
      setProgressInSeconds(seconds)
      /*
       * const progress = (100 / e.target.duration) * e.target.currentTime
       * setProgressPercentage(progress)
       */
    }
  }

  useEffect(() => {
    const current =
      subtitles &&
      subtitles.find(
        (item) =>
          item.start <= progressInSeconds && item.end >= progressInSeconds
      )
    if (current?.text !== currentSubtitle) {
      setCurrentSubtitle(current?.text)
    }
  }, [progressInSeconds, subtitles, currentSubtitle])

  const transparentButton = playing ? null : (
    <button className="play-icon-button">
      <span className="play-icon-container">
        <PlayIconBlack />
      </span>
    </button>
  )

  return (
    <VideoWrap
      className={playing ? "active" : ""}
      showPlayButtonMobile={showPlayButtonMobile}
    >
      <>
        <VideoControls>
          {hasSound && muted && (
            <MuteWrap
              onClick={handleMuteIconClick}
              className={`shadow-md ${playing ? "" : "hidden"}`}
              tabIndex="0"
              onKeyDown={(e) =>
                e.key === "Enter" ? handleMuteIconClick() : null
              }
              aria-label="Unmute video"
            >
              <MuteIcon />
            </MuteWrap>
          )}
          <PlayPauseWrap onClick={handlePlayPauseClick}>
            {showTransparentButton ? (
              transparentButton
            ) : playing ? (
              <PauseIcon className={`pause-button`} />
            ) : (
              <PlayIcon className={`play-button`} />
            )}
          </PlayPauseWrap>
        </VideoControls>
        <video
          ref={videoPlayerRef}
          autoPlay={autoplay}
          onTimeUpdate={handleProgress}
          loop={true}
          playsInline={true}
          muted={muted}
          poster={poster}
          className="video rounded"
          preload="auto"
          disablePictureInPicture={true}
        >
          <source src={video} type="video/mp4" />
        </video>
        {playing && (
          <>
            {subtitles && currentSubtitle !== 0 && (
              <Subtitle subtitle={currentSubtitle} />
            )}
          </>
        )}
      </>
    </VideoWrap>
  )
}

export default Video
